/**
 * CompanyPicker — MyGate-parity "Select company name": a quick chip row of the
 * most common companies + a searchable full-list sheet. Monogram tiles are
 * coded (no raster logos); brand colors come from the dataset as content.
 */
import React from 'react';
import { Pressable, ScrollView, View } from 'react-native';

import { radius, space } from '@bero/tokens';
import { useT } from '@/i18n';
import { useTheme } from '@/theme';
import { AppText, Chip, Input, Sheet } from '@/ui';

import { companiesFor, type Company } from './data';

function Monogram({ company, size = 40 }: { company: Company; size?: number }) {
  const fg = company.fg === 'dark' ? '#1B1B1B' : '#FFFFFF';
  return (
    <View style={{
      width: size, height: size, borderRadius: radius.md,
      backgroundColor: company.color, alignItems: 'center', justifyContent: 'center',
    }}>
      <AppText weight="700" style={{ color: fg, fontSize: size * 0.4 }}>{company.mono}</AppText>
    </View>
  );
}

/** Quick row: top picks as chips + "More…" opening the searchable sheet. */
export function CompanyChips({ kind, value, onChange, topCount = 6 }: {
  kind: 'delivery' | 'cab';
  value: string;              // '' | company name | free text (Other)
  onChange: (name: string) => void;
  topCount?: number;
}) {
  const { t } = useT();
  const all = companiesFor(kind);
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const top = all.slice(0, topCount);
  const valueInTop = top.some((c) => c.name === value);

  return (
    <>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.s2 }}>
        {top.map((c) => (
          <Chip key={c.name} label={c.name} active={value === c.name}
            onPress={() => onChange(value === c.name ? '' : c.name)} />
        ))}
        {value && !valueInTop ? <Chip label={value} active onPress={() => onChange('')} /> : null}
        <Chip label={t('cp_more')} onPress={() => setSheetOpen(true)} />
      </View>
      <CompanySheet
        kind={kind}
        visible={sheetOpen}
        onClose={() => setSheetOpen(false)}
        onSelect={(name) => { onChange(name); setSheetOpen(false); }}
      />
    </>
  );
}

export function CompanySheet({ kind, visible, onClose, onSelect }: {
  kind: 'delivery' | 'cab';
  visible: boolean;
  onClose: () => void;
  onSelect: (name: string) => void;
}) {
  const { t } = useT();
  const { colors } = useTheme();
  const [q, setQ] = React.useState('');
  const all = companiesFor(kind);
  const query = q.trim().toLowerCase();
  const results = query ? all.filter((c) => c.name.toLowerCase().includes(query)) : all;

  return (
    <Sheet visible={visible} onClose={() => { setQ(''); onClose(); }}>
      <AppText variant="headingMd" weight="500" style={{ marginBottom: space.s3 }}>
        {kind === 'cab' ? t('cp_which_cab') : t('cp_which_company')}
      </AppText>
      <Input value={q} onChangeText={setQ} placeholder={t('cp_search_ph')} autoCapitalize="none" />
      <ScrollView style={{ maxHeight: 380, marginTop: space.s3 }} keyboardShouldPersistTaps="handled">
        <View style={{ gap: 2 }}>
          {results.map((c) => (
            <Pressable
              key={c.name}
              accessibilityRole="button"
              onPress={() => { setQ(''); onSelect(c.name); }}
              style={({ pressed }) => ({
                flexDirection: 'row', alignItems: 'center', gap: space.s3,
                paddingVertical: 8, paddingHorizontal: 6, borderRadius: radius.md,
                backgroundColor: pressed ? colors.canvas1 : 'transparent',
              })}
            >
              <Monogram company={c} />
              <AppText variant="bodyMd" weight="500">{c.name}</AppText>
            </Pressable>
          ))}
          {query ? (
            <Pressable
              accessibilityRole="button"
              onPress={() => { const name = q.trim(); setQ(''); onSelect(name); }}
              style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3, paddingVertical: 10, paddingHorizontal: 6 }}
            >
              <View style={{
                width: 40, height: 40, borderRadius: radius.md, borderWidth: 1.5, borderStyle: 'dashed',
                borderColor: colors.borderFirm, alignItems: 'center', justifyContent: 'center',
              }}>
                <AppText weight="700" tone="tertiary">+</AppText>
              </View>
              <AppText variant="bodyMd" tone="secondary">{t('cp_use', { q: q.trim() })}</AppText>
            </Pressable>
          ) : null}
          {!results.length && !query ? (
            <AppText variant="bodySm" tone="tertiary">{t('cp_no_companies')}</AppText>
          ) : null}
        </View>
      </ScrollView>
    </Sheet>
  );
}
